import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { CityStoreModel, HxCityService, HxRecipeService, HxUiLabelPipe, WorkflowModel, WorkflowRequest } from 'hx-services';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxLabelEditComponent, HxWorkflowCategorySelectComponent } from 'hx-components';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NgFor, NgIf } from '@angular/common';
import { NzCheckboxComponent, NzCheckboxWrapperComponent } from 'ng-zorro-antd/checkbox';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';

@Component({
    selector: 'hc-workflow-edit',
    templateUrl: './workflow-edit.component.html',
    styleUrls: ['./workflow-edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxLabelEditComponent, NzInputDirective, HxWorkflowCategorySelectComponent, NgFor, NzCheckboxWrapperComponent, NgIf, NzCheckboxComponent, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, HxUiLabelPipe, TranslocoPipe]
})
export class WorkflowEditComponent implements OnInit {
  model!: WorkflowModel;
  isEdit = false;
  isCreate = false;
  form!: UntypedFormGroup;

  cityStores: CityStoreModel[] = [];
  isLoading = {
    model: false,
  };
  isSaving = false;
  storeIds: number[] = [];

  constructor(
    private aRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private modal: NzModalService,
    private recipeService: HxRecipeService,
    private tr: TranslocoService,
    private cityService: HxCityService,
    private notification: NzNotificationService,
    private router: Router,
  ) {
  }

  ngOnInit() {
    this.loadCityStores();
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as WorkflowModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.model = {} as WorkflowModel;
      this.isCreate = true;
    }
    this.storeIds = this.model.stores ? this.model.stores.map(r => r.id) : [];
    this.form = this.fb.group({
      title: [null, [Validators.required]],
      description: [null],
      workflowCategoryId: [null, [Validators.required]],
      storeIds: [null],
    });
    this.updateForm();
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.getRawValue() as WorkflowRequest;
      const model: WorkflowRequest = {
        id: this.model.id,
        title: formModel.title,
        description: formModel.description,
        workflowCategoryId: formModel.workflowCategoryId,
        storeIds: this.storeIds
      };

      this.isSaving = true;
      try {
        const result = await this.recipeService.saveWorkflow(model);
        this.form.markAsPristine();
        if (this.isCreate) {
          this.router.navigate([`/workflows/${result.id}/edit`]);
          this.notification.success(this.tr.translate('nav.Workflows'), this.tr.translate('nt.dataSaved'));
        } else {
          this.notification.success(this.tr.translate('nav.Workflows'), this.tr.translate('nt.dataUpdated'));
          this.isLoading.model = true;
          this.model = await this.recipeService.getWorkflowById(result.id);
          this.isLoading.model = false;
        }
      } catch (err: any) {
        this.notification.error(this.tr.translate('nav.Workflows'), this.tr.translate(err?.error?.message));
      } finally {
        this.isSaving = false;
      }
    } else {
      console.log('not valid form', this.form.errors);
    }
  }

  check(id: number) {
    const index = this.storeIds.indexOf(id);
    if (index >= 0) {
      this.storeIds.splice(index, 1);
    } else {
      this.storeIds.push(id);
    }
    this.form.markAsDirty();
  }

  private updateForm() {
    const formModel: Partial<WorkflowRequest> = {
      title: this.model.title,
      description: this.model.description,
      workflowCategoryId: this.model.workflowCategoryId,
      storeIds: this.storeIds,
    };

    this.form.patchValue(formModel);
  }

  private loadCityStores() {
    this.cityService.getCityStores({brandId: 1}).subscribe(result => {
      this.cityStores = result;
    });
  }
}
